import React, { FC, useEffect } from 'react'

import { Grid, Theme, useMediaQuery, useTheme } from '@mui/material'
import { isEmpty, isEqual } from 'lodash'
import { useTranslation } from 'react-i18next'

import { LevelOfStressWidget } from '../../../components/ReportsWidgets'
import { PopularityWidget } from '../../../components/ReportsWidgets/PopularityWidget'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { getNormalizedBarData } from '../../../utils'
import { fetchInstrumentsReport } from '../ReportsSlice'

export const InstrumentsReports: FC = () => {
  const { palette, spacing } = useTheme()
  const { t } = useTranslation()
  const matchesBreakpoints: boolean = useMediaQuery((theme: Theme) => theme.breakpoints.up(2000))
  const dispatch = useAppDispatch()

  const { period, currentAccount } = useAppSelector(({ userAccounts }) => userAccounts)
  const { instruments } = useAppSelector(({ reports }) => reports)
  const { topByMoneyNumber, topByProfitLoss, topByLevelOfStress } = instruments

  useEffect(() => {
    const periodsIsEqual =
      instruments?.meta?.period && period && isEqual(instruments.meta.period, period)
    const isNeedToUpdate =
      isEmpty(instruments) || !periodsIsEqual || currentAccount?.id !== instruments?.meta.account

    if (currentAccount?.id && isNeedToUpdate) {
      dispatch(
        fetchInstrumentsReport({
          account: currentAccount?.id,
          period: period,
        }),
      )
    }
  }, [period, currentAccount])

  const instrumentsByNumberOfTransactionsData = getNormalizedBarData<'symbol'>(
    topByMoneyNumber,
    'symbol',
    'amount',
    10,
  )
  const instrumentsByMoneyEquivalenceData = getNormalizedBarData<'symbol'>(
    topByMoneyNumber,
    'symbol',
    'money',
    10,
  )
  const instrumentsByNetProfitData = getNormalizedBarData<'symbol'>(
    topByProfitLoss,
    'symbol',
    'profit',
    10,
  )
  const instrumentsByNetLossData = getNormalizedBarData<'symbol'>(
    topByProfitLoss,
    'symbol',
    'loss',
    10,
  )
  const stressStrategiesData = getNormalizedBarData<'symbol'>(
    topByLevelOfStress,
    'symbol',
    'perCent',
    6,
  )

  return (
    <Grid container spacing={2}>
      <Grid
        item
        xs={12}
        sm={12}
        md={6}
        lg={matchesBreakpoints ? 4 : 6}
        sx={{
          pageBreakInside: 'avoid',
          display: 'flex',
          flexDirection: 'column',
          minHeight: { xs: spacing(35), sm: spacing(38), md: spacing(42.25) },
        }}
      >
        <PopularityWidget
          title={t(
            'topInstrumentsByNumberOfTransactions',
            'Top instruments by number of transactions',
          )}
          explanation={t(
            'theGraphRepresentsWhatInstrumentsHaveTheMostAmountOfTransactions',
            'The graph represents what instruments have the most amount of transactions',
          )}
          data={instrumentsByNumberOfTransactionsData}
          mainColor={palette.blue[500] as string}
        />
      </Grid>

      <Grid
        item
        xs={12}
        sm={12}
        md={6}
        lg={matchesBreakpoints ? 4 : 6}
        sx={{
          pageBreakInside: 'avoid',
          display: 'flex',
          flexDirection: 'column',
          minHeight: { xs: spacing(35), sm: spacing(38), md: spacing(42.25) },
        }}
      >
        <PopularityWidget
          title={t('topInstrumentsByMoneyEquivalence', 'Top instruments by money equivalence')}
          explanation={t(
            'theGraphRepresentsWhichInstrumentIsSpentTheMostMoneyOn',
            'The graph represents which instrument is spent the most money on',
          )}
          data={instrumentsByMoneyEquivalenceData}
          mainColor={palette.blue[500] as string}
          currency={currentAccount?.currency}
        />
      </Grid>
      <Grid
        item
        xs={12}
        sm={12}
        md={6}
        lg={matchesBreakpoints ? 4 : 6}
        sx={{
          pageBreakInside: 'avoid',
          display: 'flex',
          flexDirection: 'column',
          minHeight: { xs: spacing(35), sm: spacing(38), md: spacing(42.25) },
        }}
      >
        <PopularityWidget
          title={t('topInstrumentsByNetProfit', 'Top instruments by net profit')}
          explanation={t(
            'theGraphRepresentsWhatInstrumentsHaveTheMostNetProfit',
            'The graph represents what instruments have the most Net Profit',
          )}
          data={instrumentsByNetProfitData}
          mainColor={palette.green[400] as string}
          currency={currentAccount?.currency}
        />
      </Grid>
      <Grid
        item
        xs={12}
        sm={12}
        md={6}
        lg={matchesBreakpoints ? 4 : 6}
        sx={{
          pageBreakInside: 'avoid',
          display: 'flex',
          flexDirection: 'column',
          minHeight: { xs: spacing(35), sm: spacing(38), md: spacing(42.25) },
        }}
      >
        <PopularityWidget
          title={t('topInstrumentsByNetLoss', 'Top instruments by net loss')}
          explanation={t(
            'theGraphRepresentsWhatInstrumentsHaveMostNetLoss',
            'The graph represents what instruments have the most Net Loss',
          )}
          data={instrumentsByNetLossData}
          mainColor={palette.red[400] as string}
          currency={currentAccount?.currency}
        />
      </Grid>
      <Grid
        item
        xs={12}
        sm={12}
        md={6}
        lg={matchesBreakpoints ? 4 : 6}
        sx={{
          pageBreakInside: 'avoid',
          display: 'flex',
          flexDirection: 'column',
          minHeight: { xs: spacing(35), sm: spacing(38), md: spacing(42.25) },
        }}
      >
        <LevelOfStressWidget
          title={t('topInstrumentsByLevelOfStress', 'Top instruments by level of stress')}
          explanation={t(
            'theGraphRepresentsWhatAreTheMostStressfulInstruments',
            'The graph represents what are the most stressful instruments',
          )}
          data={stressStrategiesData}
          minZone={30}
          maxZone={60}
        />
      </Grid>
    </Grid>
  )
}
